/**
 * verse/whispers/compose.ts — Clause composition engine
 *
 * The manifesto says: "VERSE is a transformation — the input passes
 * through a mathematical process and emerges as something that belongs
 * to the conversation. The space of possible responses is not a library.
 * It is a field."
 *
 * And: "the player's own words contain the raw material for the response.
 * A response that grows from that material feels like understanding,
 * because it is built from the shape of how they think."
 *
 * This engine has NO static string arrays. Every follow-through is
 * CONSTRUCTED by a generative frame function from fill-word vocabularies.
 * The output was never written by anyone — it emerges from the process.
 *
 * CRITICAL: The word flows through the ENTIRE composition, not just the
 * base clause. Follow-throughs receive the word's nominal form and use
 * it as a CATEGORY LABEL — "the grief doesn't fade", "the sadness has
 * edges". This is word-safe because the word is named, not acting.
 * And it makes the response GROW FROM THE WORD, not just from affect.
 *
 * Architecture:
 *   1. BASE clause — the word in a grammatical frame. Constructed from
 *      the word. 6 options per word type.
 *   2. FOLLOW-THROUGH — generated by a frame function that takes the
 *      word's nominal form AND fill words from affect-selected vocabularies.
 *      Each frame is a grammatical transformation that threads the word
 *      through the entire thought. Nobody wrote these sentences.
 *   3. CONNECTOR — how base and follow-through join. 3 options,
 *      selected by prosody state.
 *
 * Output space per word type:
 *   bases(6) × frames(11) × fills_per_frame(~25 avg) × connectors(3)
 *   = ~4,500 unique responses per word type
 *   × unique word = INFINITE (every word produces different follow-throughs)
 *
 * Over 30 turns, exact repetition probability < 0.1%.
 */

import { cap, articleFor } from "./types.js";
import { embedQuery } from "../../neural/embeddings.js";
import { cosine } from "../../neural/math.js";

// ─── Base Clause Generators ──────────────────────────────────────────────────

export type BaseClause = string;

/** Adjective base clauses — 6 grammatical stances */
export function adjBase(word: string, rand: () => number): BaseClause {
  const W = cap(word);
  const bases = [
    `${W} — yeah.`,               // bare with pause
    `That's ${word}, right there.`, // identification
    `Something about the ${word}.`, // indirect
    `Really ${word}. I feel that.`, // intensification
    `Yeah. ${W}.`,                  // acknowledged
    `That ${word} is real.`,        // witnessed
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Noun base clauses — all 3+ words to avoid hollow outputs */
export function nounBase(word: string, rand: () => number): BaseClause {
  const art = articleFor(word);
  const bases = [
    `${art} ${word} matters.`,                // acknowledged weight
    `That ${word} — yeah.`,                   // deictic with pause
    `Something about the ${word}.`,           // indirect presence
    `Yeah. ${art} ${word}.`,                  // acknowledged
    `That's the ${word} in this.`,            // identification
    `Right — the ${word}.`,                   // confirmation
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Gerund base clauses — all 3+ words */
export function gerundBase(gerund: string, rand: () => number): BaseClause {
  const bases = [
    `The ${gerund} is real.`,                // acknowledged
    `That ${gerund} — I see it.`,            // deictic with witness
    `Something about the ${gerund}.`,        // indirect
    `That's the ${gerund} right there.`,     // identification
    `Right — the ${gerund}.`,                // confirmation
  ];
  return bases[Math.floor(rand() * bases.length)];
}

// ─── Generative Follow-Through Frames ────────────────────────────────────────
//
// These are TRANSFORMATIONS, not templates. Each frame is a function that
// takes the word's nominal form (the category label) AND fill words from
// affect-selected vocabularies, then CONSTRUCTS a novel follow-through.
//
// WORD-DERIVATION: The word's nominal form threads through the entire
// composition. "Grief" → "the grief doesn't fade". "Joy" → "the joy
// doesn't fade". The SAME frame produces DIFFERENT content for DIFFERENT
// words. This is what makes the response grow from the player's words.
//
// Word-safety: the word is used as a CATEGORY LABEL ("the grief", "the
// sadness", "the falling") — it's NAMED, not ACTING. "The grief doesn't
// fade" = the category of grief persists. This works for any word.

export type FollowThrough = string;

/** The word's derived form for use in follow-throughs.
 *  Adjectives → "the {nom}" (the sadness, the grief)
 *  Nouns → "the {word}" (the grief, the hope)
 *  Gerunds → "the {gerund}" (the falling, the breaking)
 *  This is always a category label — word-safe. */
export type NominalForm = string;

// ── Fill-word vocabularies ────────────────────────────────────────────────────
// These are NOT response strings. They are grammatical SLOT FILLERS.
// A verb that fits "doesn't {X}" or a noun that fits "has {X}".
// They are small, composable primitives — the atoms of the field.
//
// SEMANTIC COHERENCE: Fill words are selected by embedding similarity
// to the input word, not randomly. "Grief" selects "doesn't fade" (high
// coherence) over "doesn't dissolve" (lower). "Joy" selects "doesn't just
// visit" (joy visits — that's coherent). The composition REASONS about
// which fill words make semantic sense with the input word.

const DIMINISH_VERBS = [
  "shrink", "fade", "lessen", "dissolve", "retreat",
  "quiet down", "soften", "diminish", "disappear", "go quiet",
];

const PERCEPTION_VERBS = [
  "see", "notice", "name", "look at", "acknowledge",
  "point at", "say out loud",
];

const QUALIFYING_ADJS = [
  "real", "deep", "lasting", "heavy", "true", "raw", "genuine",
  "weighty", "unmistakable",
];

const NEGATING_ADJS = [
  "surface", "small", "easy", "light", "performative", "convenient",
  "polite", "comfortable",
];

const HOLD_VERBS = [
  "sitting with", "holding", "staying with", "keeping",
  "not moving past", "not letting go of", "keeping close",
  "receiving", "holding carefully", "staying near",
];

const HOLD_PREFIXES = ["I'm", "I want to be"];

const EXIT_VERBS = [
  "leave", "go", "pass", "fade", "finish", "end",
  "depart", "dissolve",
];

const ENTER_VERBS = ["came", "arrived", "started", "appeared", "entered"];

const EDGE_NOUNS = [
  "edges", "weight", "teeth", "gravity", "presence",
  "a shape you can't ignore", "consequences", "a cost",
];

const CONSEQUENCE_VERBS = [
  "shows", "matters", "changes what comes next", "doesn't arrive alone",
  "rearranges things", "doesn't just visit", "doesn't just pass",
  "doesn't slow down just because you noticed it",
  "doesn't ask permission", "doesn't negotiate", "doesn't wait",
];

const CURIOSITY_VERBS = ["asks", "points at", "opens", "reaches toward"];

const CURIOSITY_TARGETS = [
  "something", "a door", "a question bigger than the answer",
  "something underneath", "something that wants to be found",
];

const VALIDATION_NOUNS = [
  "center", "heart", "core", "crux", "weight",
  "real thing", "truest part", "load-bearing part",
];

const IMPACT_VERBS = [
  "doesn't go away", "landed", "carries weight",
  "doesn't leave fast", "stays with me",
  "doesn't just visit", "doesn't just pass",
  "doesn't arrive alone", "doesn't slow down",
];

const QUIET_PHRASES = [
  "Just that.", "Nothing to add.", "I'll be quiet with that.",
  "Just that for now.", "Nothing more needed.", "That's enough.",
];

const PERSISTENCE_ADJS = [
  "stays", "doesn't leave", "lingers",
  "has its own timing", "outlasts the moment",
  "doesn't finish on schedule",
];

const NAMING_CONSEQUENCES = [
  "doesn't make it smaller", "doesn't make it lighter",
  "doesn't shrink it", "doesn't contain it",
  "doesn't reduce it", "doesn't resolve it",
];

// ── Frame functions ───────────────────────────────────────────────────────────
// Each frame is a grammatical transformation. It takes the word's nominal
// form AND fill words, producing a novel follow-through that GROWS FROM
// THE WORD. The word appears as a category label — word-safe.

/** Frame: And the {nom} doesn't {dim} just because you {perc} it.
 *  Affect: depth. Output space: 10 × 7 = 70 PER WORD */
function negateDiminish(nom: NominalForm, dim: string, perc: string): FollowThrough {
  return `And the ${nom} doesn't ${dim} just because you ${perc} it.`;
}

/** Frame: The {qual} {nom}, not the {neg_qual} version.
 *  Affect: depth. Output space: 9 × 8 = 72 PER WORD */
function typeSpecify(nom: NominalForm, qual: string, negQual: string): FollowThrough {
  return `The ${qual} ${nom}, not the ${negQual} version.`;
}

/** Frame: {prefix} {hold_verb} the {nom}.
 *  Affect: warmth. Output space: 2 × 10 = 20 PER WORD */
function commitHold(nom: NominalForm, prefix: string, hold: string): FollowThrough {
  return `${prefix} ${hold} the ${nom}.`;
}

/** Frame: The {nom} doesn't {exit} the way it {enter}.
 *  Affect: melancholy. Output space: 8 × 5 = 40 PER WORD */
function temporalPersist(nom: NominalForm, exit: string, enter: string): FollowThrough {
  return `The ${nom} doesn't ${exit} the way it ${enter}.`;
}

/** Frame: The {nom} has {edge}.
 *  Affect: tension. Output space: 8 PER WORD */
function edgeDeclare(nom: NominalForm, edge: string): FollowThrough {
  return `The ${nom} has ${edge}.`;
}

/** Frame: And the {nom} {consequence}.
 *  Affect: tension/depth. Output space: 11 PER WORD */
function consequential(nom: NominalForm, consequence: string): FollowThrough {
  return `And the ${nom} ${consequence}.`;
}

/** Frame: And the {nom} {verb} {target}.
 *  Affect: curiosity. Output space: 4 × 5 = 20 PER WORD */
function curiosityFrame(nom: NominalForm, verb: string, target: string): FollowThrough {
  return `And the ${nom} ${verb} ${target}.`;
}

/** Frame: That's the {validation} — the {nom}.
 *  Affect: baseline. Output space: 8 PER WORD */
function namedValidate(nom: NominalForm, validation: string): FollowThrough {
  return `That's the ${validation} — the ${nom}.`;
}

/** Frame: The {nom} {impact}.
 *  Affect: baseline. Output space: 8 PER WORD */
function resonantImpact(nom: NominalForm, impact: string): FollowThrough {
  return `The ${nom} ${impact}.`;
}

/** Frame: And naming the {nom} {naming_consequence}.
 *  Affect: melancholy/depth. Output space: 6 PER WORD */
function namingConsequence(nom: NominalForm, consequence: string): FollowThrough {
  return `And naming the ${nom} ${consequence}.`;
}

/** Frame: The {nom} that {persistence_adj}.
 *  Affect: melancholy. Output space: 5 PER WORD */
function persistenceKind(nom: NominalForm, adj: string): FollowThrough {
  return `The ${nom} ${adj}.`;
}

/** Frame: {quiet_phrase} — the {nom}.
 *  Affect: quiet. Even silence carries the word. Output space: 6 PER WORD */
function quietDeclare(nom: NominalForm, phrase: string): FollowThrough {
  // Short quiet phrases that end with period — append the nominal for grounding
  // "Just that. The grief." — the word is present even in stillness
  return `${phrase} The ${nom}.`;
}

// ── Frame registry ───────────────────────────────────────────────────────────

interface FrameDef {
  /** Frame function — first arg is always the nominal form */
  fn: (nom: NominalForm, ...fills: string[]) => FollowThrough;
  /** Vocabulary arrays for each fill slot (after nom) */
  vocabs: string[][];
  /** Which affect dimension gates this frame */
  affect: "depth" | "tension" | "warmth" | "melancholy" | "curiosity" | "quiet" | "baseline";
  /** Threshold for the gating affect dimension */
  threshold: number;
}

const FRAMES: FrameDef[] = [
  // ── Depth frames ──
  { fn: negateDiminish, vocabs: [DIMINISH_VERBS, PERCEPTION_VERBS], affect: "depth", threshold: 0.5 },
  { fn: typeSpecify, vocabs: [QUALIFYING_ADJS, NEGATING_ADJS], affect: "depth", threshold: 0.5 },
  { fn: namingConsequence, vocabs: [NAMING_CONSEQUENCES], affect: "depth", threshold: 0.5 },

  // ── Tension frames ──
  { fn: edgeDeclare, vocabs: [EDGE_NOUNS], affect: "tension", threshold: 0.6 },
  { fn: consequential, vocabs: [CONSEQUENCE_VERBS], affect: "tension", threshold: 0.55 },

  // ── Warmth frames ──
  { fn: commitHold, vocabs: [HOLD_PREFIXES, HOLD_VERBS], affect: "warmth", threshold: 0.5 },

  // ── Melancholy frames ──
  { fn: temporalPersist, vocabs: [EXIT_VERBS, ENTER_VERBS], affect: "melancholy", threshold: 0.5 },
  { fn: persistenceKind, vocabs: [PERSISTENCE_ADJS], affect: "melancholy", threshold: 0.5 },

  // ── Curiosity frames ──
  { fn: curiosityFrame, vocabs: [CURIOSITY_VERBS, CURIOSITY_TARGETS], affect: "curiosity", threshold: 0.5 },

  // ── Quiet frames ──
  { fn: quietDeclare, vocabs: [QUIET_PHRASES], affect: "quiet", threshold: 0 },

  // ── Baseline frames (always available) ──
  { fn: namedValidate, vocabs: [VALIDATION_NOUNS], affect: "baseline", threshold: 0 },
  { fn: resonantImpact, vocabs: [IMPACT_VERBS], affect: "baseline", threshold: 0 },
];

// ── Follow-through selection ──────────────────────────────────────────────────

export interface ComposeContext {
  depth: number;
  tension: number;
  warmth: number;
  melancholy: number;
  curiosity: number;
  intensity: number;
  patience: number;
}

/** Select a follow-through based on affect state — constructs a novel
 *  sentence from a generative frame, threading the word's nominal form
 *  through the entire composition. The response GROWS FROM THE WORD. */
export function selectFollowThrough(
  nom: NominalForm,
  ctx: ComposeContext,
  rand: () => number,
): FollowThrough | null {
  // 20% chance of NO follow-through — the base alone is powerful
  if (rand() < 0.20) return null;

  // Filter frames by affect state
  const activeFrames = FRAMES.filter(f => {
    if (f.affect === "baseline") return true;
    if (f.affect === "quiet") return ctx.intensity < 0.35 && ctx.patience > 0.5;
    const val = ctx[f.affect];
    return val > f.threshold;
  });

  // Fallback: if no affect-gated frames activated, use depth + baseline
  if (activeFrames.length <= 2) { // only baseline frames
    const depthFrames = FRAMES.filter(f => f.affect === "depth");
    activeFrames.push(...depthFrames);
  }

  // Select a frame
  const frame = activeFrames[Math.floor(rand() * activeFrames.length)];

  // Fill each vocabulary slot — SEMANTIC COHERENCE: rank fill words by
  // embedding similarity to the input word, then select from top-N.
  // This makes the composition REASON about which fill words make sense.
  const nomEmbed = embedQuery(nom);
  const fills = frame.vocabs.map(vocab => {
    // Rank by semantic similarity to the input word
    const ranked = vocab.map((fill, i) => {
      // Embed the fill word in context of the frame to get better similarity
      const fillEmbed = embedQuery(fill);
      const sim = cosine(nomEmbed, fillEmbed);
      return { fill, sim, idx: i };
    }).sort((a, b) => b.sim - a.sim);

    // Select from top-N with PRNG variation — not always the #1 pick.
    // Top-4 gives variety while maintaining semantic coherence.
    // When vocab is small (≤4), use all of them.
    const topN = Math.min(4, ranked.length);
    const pick = ranked[Math.floor(rand() * topN)];
    return pick.fill;
  });

  // Construct the follow-through — threading the word through the frame
  return frame.fn(nom, ...fills);
}

// ─── Connectors ──────────────────────────────────────────────────────────────

export type Connector = string;

/** Select a connector based on prosody state */
export function selectConnector(
  ctx: { patience: number; intensity: number },
  rand: () => number,
): Connector {
  const roll = rand();
  // High patience → more trailing/contemplative connectors
  if (ctx.patience > 0.5 && roll < 0.25) return "... ";
  // High intensity → more em-dash (punchy connection)
  if (ctx.intensity > 0.5 && roll < 0.3) return " — ";
  // Default: period (clean separation)
  return ". ";
}

// ─── Full Composition ────────────────────────────────────────────────────────

/** Compose a full echo from base + optional follow-through + connector */
export function composeEcho(
  base: BaseClause,
  follow: FollowThrough | null,
  connector: Connector,
): string {
  // When follow-through is null, the base alone is hollow ("This wanting." = 2 words).
  // Use a default presence follow-through to ensure 3+ word output.
  if (!follow) {
    const cleanBase = base.replace(/[.…]+$/, "");
    return `${cleanBase} — I'm here with that.`;
  }
  // Strip trailing punctuation from base before adding connector
  const cleanBase = base.replace(/[.…]+$/, "");
  return cleanBase + connector + follow;
}

/** High-tension composition — always bare base, minimal follow-through */
export function composeTensionEcho(
  base: BaseClause,
  follow: FollowThrough | null,
): string {
  if (!follow) {
    const cleanBase = base.replace(/[.…]+$/, "");
    return `${cleanBase}. Right here.`;
  }
  const cleanBase = base.replace(/[.…]+$/, "");
  // Tension echoes use period — no soft connectors
  return cleanBase + ". " + follow;
}

// ─── Generative Frames for All Operators ─────────────────────────────────────
//
// The manifesto says: "Never add templates. If you find yourself writing a list
// of possible responses keyed on intent, stop. That is a template."
//
// These frame functions replace the whisper library pick-from-pool calls.
// Each one constructs a response from grammatical primitives + the input word,
// using embedding similarity for semantic coherence. The output was never
// written by anyone — it emerges from the process.

// ── Tension Echo Frames ──────────────────────────────────────────────────────
// High-tension echoes: brief, maximal impact. The word + a short presence signal.

const TENSION_PRESENCE = [
  "Right there.", "Yeah.", "I see it.", "That's real.", "That landed hard.",
  "I'm not looking away.", "I felt that.", "That's not small.",
];

const TENSION_SOMATIC = [
  "I felt that in my chest.", "That one's in the bones.",
  "That landed in the gut.", "I felt that in my throat.",
];

const TENSION_QUIET = [
  "Just —", "Nothing else right now.", "Just that.",
];

/** Generative high-tension adjective echo */
export function tensionAdjEcho(word: string, rand: () => number): string {
  const W = cap(word);
  const nomEmbed = embedQuery(word);

  // Rank presence signals by semantic coherence
  const allSignals = [...TENSION_PRESENCE, ...TENSION_SOMATIC, ...TENSION_QUIET];
  const ranked = allSignals.map(s => ({
    signal: s,
    sim: cosine(nomEmbed, embedQuery(s.replace(/[^a-zA-Z ]/g, "").trim() || "that")),
  })).sort((a, b) => b.sim - a.sim);

  const topN = Math.min(4, ranked.length);
  const pick = ranked[Math.floor(rand() * topN)];

  // Construct: "Word. {presence_signal}"
  return `${W}. ${pick.signal}`;
}

/** Generative high-tension gerund echo */
export function tensionGerundEcho(gerund: string, rand: () => number): string {
  const G = cap(gerund);
  const nomEmbed = embedQuery(gerund);

  const allSignals = [...TENSION_PRESENCE, ...TENSION_SOMATIC, ...TENSION_QUIET];
  const ranked = allSignals.map(s => ({
    signal: s,
    sim: cosine(nomEmbed, embedQuery(s.replace(/[^a-zA-Z ]/g, "").trim() || "that")),
  })).sort((a, b) => b.sim - a.sim);

  const topN = Math.min(4, ranked.length);
  const pick = ranked[Math.floor(rand() * topN)];

  return `${G}. ${pick.signal}`;
}

// ── Two-Word Echo Frames ─────────────────────────────────────────────────────
// Two-word echoes: the pair + a connection signal.

const TWO_WORD_CONNECTORS = [
  "Yeah.", "That's it.", "I see both.", "Together.", "That's the whole thing.",
  "Both of those.", "That's the shape of it.", "Right.",
];

/** Generative two-word echo — noun first */
export function twoWordNounFirst(noun: string, rand: () => number): string {
  const N = cap(noun);
  const art = articleFor(noun);
  const bases = [
    `${N}. Yeah.`, `${art} ${noun}.`, `That ${noun}.`, `Yeah — ${noun}.`,
    `${N}. Right.`,
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Generative two-word echo — adjective first */
export function twoWordAdjFirst(adj: string, rand: () => number): string {
  const A = cap(adj);
  const bases = [
    `${A}. Yeah.`, `That ${adj}.`, `Really ${adj}.`, `${A}. Right.`,
    `That's ${adj}.`,
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Generative two-word echo — noun second */
export function twoWordNounSecond(noun: string, rand: () => number): string {
  const art = articleFor(noun);
  const bases = [
    `Yeah. ${art} ${noun}.`, `That ${noun}.`, `${cap(noun)}.`,
    `Right. ${art} ${noun}.`,
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Generative two-word echo — adjective second */
export function twoWordAdjSecond(adj: string, rand: () => number): string {
  const bases = [
    `That's ${adj}.`, `Really ${adj}.`, `${cap(adj)}.`, `Yeah — ${adj}.`,
  ];
  return bases[Math.floor(rand() * bases.length)];
}

/** Generative two-word echo — generic */
export function twoWordGeneric(first: string, second: string, gerund: string, rand: () => number): string {
  const bases = [
    `The ${gerund}. Yeah.`, `That ${first} ${second}.`, `${cap(first)} ${second}.`,
    `Yeah — ${first} and ${second}.`, `The ${gerund}. Right.`,
  ];
  return bases[Math.floor(rand() * bases.length)];
}

// ── Nominalize Frames ────────────────────────────────────────────────────────
// Nominalize converts a word into a noun form and gives it presence.

const GERUND_FRAMES = [
  (nom: string): string => `The ${nom}.`,
  (nom: string): string => `That ${nom}.`,
  (nom: string): string => `Yeah. The ${nom}.`,
  (nom: string): string => `${cap(nom)}.`,
  (nom: string): string => `This ${nom}.`,
];

const ABSTRACT_NOUN_FRAMES = [
  (nom: string): string => `The ${nom}.`,
  (nom: string): string => `That ${nom}.`,
  (nom: string): string => `Yeah. The ${nom}.`,
  (nom: string): string => `${cap(nom)}.`,
  (nom: string): string => `That's the ${nom}.`,
];

const IRREGULAR_NOUN_FRAMES = [
  (nom: string): string => `The ${nom}.`,
  (nom: string): string => `That ${nom}.`,
  (nom: string): string => `Yeah. The ${nom}.`,
  (nom: string): string => `${cap(nom)}.`,
  (nom: string): string => `That's ${nom}.`,
];

/** Generative gerund nominalize base */
export function gerundNomBase(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = GERUND_FRAMES.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

/** Generative abstract noun nominalize base */
export function abstractNounBase(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = ABSTRACT_NOUN_FRAMES.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

/** Generative irregular noun nominalize base */
export function irregularNounBase(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = IRREGULAR_NOUN_FRAMES.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

// ── Temporal Hold Frames ─────────────────────────────────────────────────────
// Temporal hold: slow the kernel down, hold it in time, make space around it.
// These are short (≤18 chars), no trailing period.

const TEMPORAL_PAST_FRAMES = [
  (nom: string): string => `I'm still here with it`,
  (nom: string): string => `The ${nom} stays`,
  (nom: string): string => `Hasn't left. I'm not leaving`,
  (nom: string): string => `Still present. Not going anywhere`,
  (nom: string): string => `The ${nom} remains`,
];

const TEMPORAL_DEEP_FRAMES = [
  (nom: string): string => `Holding the ${nom}`,
  (nom: string): string => `Staying with it`,
  (nom: string): string => `Not moving past`,
  (nom: string): string => `The ${nom} has time`,
];

const TEMPORAL_TENSION_FRAMES = [
  (nom: string): string => `Right here, right now`,
  (nom: string): string => `This moment. I'm in it`,
  (nom: string): string => `The ${nom} is here`,
  (nom: string): string => `Present`,
];

const TEMPORAL_MELANCHOLY_FRAMES = [
  (nom: string): string => `The ${nom} lingers`,
  (nom: string): string => `It stays`,
  (nom: string): string => `Not leaving yet`,
  (nom: string): string => `The ${nom} remains`,
];

const TEMPORAL_WARMTH_FRAMES = [
  (nom: string): string => `Staying with this`,
  (nom: string): string => `The ${nom} has room`,
  (nom: string): string => `Holding the ${nom}`,
  (nom: string): string => `Space for the ${nom}`,
];

const TEMPORAL_DEFAULT_FRAMES = [
  (nom: string): string => `Holding`,
  (nom: string): string => `The ${nom} is here`,
  (nom: string): string => `Staying with it`,
  (nom: string): string => `Present`,
  (nom: string): string => `The ${nom} has time`,
];

/** Generative temporal hold — word-derived, ≤18 chars, no trailing period */
export function temporalHold(nom: string, affect: "past" | "deep" | "tension" | "melancholy" | "warmth" | "default", rand: () => number): string {
  const frameSets: Record<string, Array<(n: string) => string>> = {
    past: TEMPORAL_PAST_FRAMES,
    deep: TEMPORAL_DEEP_FRAMES,
    tension: TEMPORAL_TENSION_FRAMES,
    melancholy: TEMPORAL_MELANCHOLY_FRAMES,
    warmth: TEMPORAL_WARMTH_FRAMES,
    default: TEMPORAL_DEFAULT_FRAMES,
  };
  const frames = frameSets[affect];
  const nomEmbed = embedQuery(nom);
  const ranked = frames.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  let result = ranked[Math.floor(rand() * topN)].output;
  // Enforce ≤18 chars, no trailing period
  result = result.replace(/\.$/, "");
  if (result.length > 18) result = result.slice(0, 18).replace(/\s\w*$/, "");
  return result;
}

// ── Invert Frames ────────────────────────────────────────────────────────────
// Invert: find the semantic complement, approach from the other side.

const INVERT_NEGATION_FLIP = [
  (target: string): string => `What would ${target} look like?`,
  (target: string): string => `If ${target} was possible — what then?`,
  (target: string): string => `What would it take to ${target}?`,
  (target: string): string => `Where does ${target} start?`,
];

const INVERT_AFFIRMING = [
  (nom: string): string => `There's another side to the ${nom}.`,
  (nom: string): string => `The ${nom} isn't the whole picture.`,
  (nom: string): string => `Something else is true here too.`,
];

const INVERT_QUESTIONING = [
  (nom: string): string => `What's on the other side of the ${nom}?`,
  (nom: string): string => `What does the ${nom} leave out?`,
  (nom: string): string => `If the ${nom} wasn't here, what would be?`,
  (nom: string): string => `What's the ${nom} covering?`,
];

const INVERT_REFRAMING = [
  (nom: string): string => `The ${nom} is real. And so is what's next to it.`,
  (nom: string): string => `The ${nom} is one angle. There's another.`,
  (nom: string): string => `The ${nom} has a shadow. The shadow has something to say.`,
];

/** Generative invert — negation flip */
export function invertNegationFlip(target: string, rand: () => number): string {
  const targetEmbed = embedQuery(target);
  const ranked = INVERT_NEGATION_FLIP.map(f => {
    const output = f(target);
    return { output, sim: cosine(targetEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || target)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

/** Generative invert — affirming (word-independent presence) */
export function invertAffirming(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = INVERT_AFFIRMING.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(2, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

/** Generative invert — questioning */
export function invertQuestioning(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = INVERT_QUESTIONING.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(3, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

/** Generative invert — reframing */
export function invertReframing(nom: string, rand: () => number): string {
  const nomEmbed = embedQuery(nom);
  const ranked = INVERT_REFRAMING.map(f => {
    const output = f(nom);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(2, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

// ── Situate Body Frames ─────────────────────────────────────────────────────
// SITUATE_BODY: ground an abstract concept in somatic metaphor.

const SITuate_DEEP = [
  (nom: string, loc: string): string => `The ${nom} sits in the ${loc}. I can feel it there.`,
  (nom: string, loc: string): string => `The ${nom} has weight in the ${loc}.`,
  (nom: string, loc: string): string => `The ${nom} lives in the ${loc}. That's where it registers.`,
];

const SITuate_TENSION = [
  (nom: string, loc: string): string => `The ${nom} — I feel it in my ${loc}.`,
  (nom: string, loc: string): string => `The ${nom} is in the ${loc}. Right now.`,
  (nom: string, loc: string): string => `${cap(nom)}. I can feel it in my ${loc}.`,
];

const SITuate_WARMTH = [
  (nom: string, loc: string): string => `The ${nom} is warm in the ${loc}.`,
  (nom: string, loc: string): string => `I can feel the ${nom} in my ${loc}. It's gentle.`,
  (nom: string, loc: string): string => `The ${nom} settles in the ${loc}. Softly.`,
];

const SITuate_DEFAULT = [
  (nom: string, loc: string): string => `The ${nom} is in the ${loc}.`,
  (nom: string, loc: string): string => `I feel the ${nom} in my ${loc}.`,
  (nom: string, loc: string): string => `The ${nom} registers in the ${loc}.`,
];

/** Generative situate body — word + location derived from affect */
export function situateBody(nom: string, loc: string, affect: "deep" | "tension" | "warmth" | "default", rand: () => number): string {
  const frameSets: Record<string, Array<(n: string, l: string) => string>> = {
    deep: SITuate_DEEP,
    tension: SITuate_TENSION,
    warmth: SITuate_WARMTH,
    default: SITuate_DEFAULT,
  };
  const frames = frameSets[affect];
  const nomEmbed = embedQuery(nom);
  const ranked = frames.map(f => {
    const output = f(nom, loc);
    return { output, sim: cosine(nomEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || nom)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(2, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

// ── Compress Frames ──────────────────────────────────────────────────────────
// COMPRESS: distill a complex kernel to its irreducible core.

const COMPRESS_SINGLE_KNOWN = [
  (word: string): string => `${cap(word)}.`,
  (word: string): string => `Just ${word}.`,
  (word: string): string => `${cap(word)}. That's the core.`,
  (word: string): string => `${cap(word)}. Nothing else needed.`,
];

const COMPRESS_SINGLE_GERUND = [
  (nom: string): string => `The ${nom}.`,
  (nom: string): string => `Just the ${nom}.`,
  (nom: string): string => `${cap(nom)}. That's it.`,
];

const COMPRESS_RELATIONAL = [
  (a: string, b: string, nom: string): string => `${cap(nom)} about your ${b}.`,
  (a: string, b: string, nom: string): string => `The ${nom} — about ${b}.`,
];

const COMPRESS_NOUN_SECOND = [
  (a: string, b: string, nom: string): string => `${cap(nom)} the ${b}.`,
  (a: string, b: string, nom: string): string => `The ${nom} of the ${b}.`,
];

const COMPRESS_GENERIC_TWO = [
  (a: string, b: string): string => `${cap(a)} ${b}.`,
  (a: string, b: string): string => `The ${a} and the ${b}.`,
  (a: string, b: string): string => `${cap(a)}. And ${b}.`,
];

/** Generative compress — single known word */
export function compressSingleKnown(word: string, rand: () => number): string {
  return COMPRESS_SINGLE_KNOWN[Math.floor(rand() * COMPRESS_SINGLE_KNOWN.length)](word);
}

/** Generative compress — single gerund */
export function compressSingleGerund(nom: string, rand: () => number): string {
  return COMPRESS_SINGLE_GERUND[Math.floor(rand() * COMPRESS_SINGLE_GERUND.length)](nom);
}

/** Generative compress — relational */
export function compressRelational(a: string, b: string, nom: string, rand: () => number): string {
  return COMPRESS_RELATIONAL[Math.floor(rand() * COMPRESS_RELATIONAL.length)](a, b, nom);
}

/** Generative compress — noun second */
export function compressNounSecond(a: string, b: string, nom: string, rand: () => number): string {
  return COMPRESS_NOUN_SECOND[Math.floor(rand() * COMPRESS_NOUN_SECOND.length)](a, b, nom);
}

/** Generative compress — generic two */
export function compressGenericTwo(a: string, b: string, rand: () => number): string {
  return COMPRESS_GENERIC_TWO[Math.floor(rand() * COMPRESS_GENERIC_TWO.length)](a, b);
}

// ── Qualify Soft Frames ──────────────────────────────────────────────────────
// QUALIFY_SOFT: add epistemic softness without undermining commitment.

const QUALIFY_PHILOSOPHICAL = [
  (core: string): string => `I don't know if the ${core} is the whole answer. But it's part of it.`,
  (core: string): string => `The ${core} might be the beginning. It's not the end.`,
  (core: string): string => `The ${core} is a direction. Not a destination.`,
];

const QUALIFY_TENSION = [
  (core: string): string => `The ${core}. I'm certain of that.`,
  (core: string): string => `${cap(core)}. That's clear.`,
  (core: string): string => `The ${core} is real. That much I know.`,
];

const QUALIFY_WARM = [
  (core: string): string => `The ${core} — I feel it too.`,
  (core: string): string => `The ${core} is here. I'm staying with it.`,
  (core: string): string => `The ${core}. Gently.`,
];

const QUALIFY_MELANCHOLY = [
  (core: string): string => `The ${core} has weight. I'm not trying to lift it.`,
  (core: string): string => `The ${core} deserves to be here.`,
  (core: string): string => `The ${core} is real. I won't look away.`,
];

const QUALIFY_DEFAULT = [
  (core: string): string => `The ${core}. I mean it.`,
  (core: string): string => `${cap(core)}. That's what's true.`,
  (core: string): string => `The ${core} is here.`,
  (core: string): string => `That's the ${core}. And it counts.`,
];

/** Generative qualify soft */
export function qualifySoft(core: string, affect: "philosophical" | "tension" | "warm" | "melancholy" | "default", rand: () => number): string {
  const frameSets: Record<string, Array<(c: string) => string>> = {
    philosophical: QUALIFY_PHILOSOPHICAL,
    tension: QUALIFY_TENSION,
    warm: QUALIFY_WARM,
    melancholy: QUALIFY_MELANCHOLY,
    default: QUALIFY_DEFAULT,
  };
  const frames = frameSets[affect];
  const coreEmbed = embedQuery(core);
  const ranked = frames.map(f => {
    const output = f(core);
    return { output, sim: cosine(coreEmbed, embedQuery(output.replace(/[^a-zA-Z ]/g, "").trim() || core)) };
  }).sort((a, b) => b.sim - a.sim);
  const topN = Math.min(2, ranked.length);
  return ranked[Math.floor(rand() * topN)].output;
}

// ── Interrogate Frames ───────────────────────────────────────────────────────
// INTERROGATE: convert a statement kernel into a question that deepens it.

const INTERROGATE_VERB_LIKE = [
  (target: string, content: string[]): string => `Where does ${target} take you?`,
  (target: string, content: string[]): string => `What does ${target} feel like?`,
  (target: string, content: string[]): string => `How does ${target} change things?`,
  (target: string, content: string[]): string => `What would ${target} look like?`,
];

const INTERROGATE_PHENOMENOLOGICAL = [
  (qWord: string): string => `What does the ${qWord} feel like from inside?`,
  (qWord: string): string => `Where does the ${qWord} live in your body?`,
  (qWord: string): string => `What shape does the ${qWord} take?`,
];

const INTERROGATE_TEMPORAL = [
  (qWord: string): string => `When did the ${qWord} start?`,
  (qWord: string): string => `How long has the ${qWord} been here?`,
  (qWord: string): string => `What comes after the ${qWord}?`,
];

const INTERROGATE_SPATIAL = [
  (qWord: string): string => `Where is the ${qWord} right now?`,
  (qWord: string): string => `What's next to the ${qWord}?`,
  (qWord: string): string => `What's the ${qWord} sitting on top of?`,
];

const INTERROGATE_DEEPENING = [
  (qWord: string): string => `What's underneath the ${qWord}?`,
  (qWord: string): string => `What does the ${qWord} want?`,
  (qWord: string): string => `What is the ${qWord} protecting?`,
];

const INTERROGATE_CHALLENGING = [
  (): string => `Is that the whole truth?`,
  (): string => `What aren't you saying?`,
  (): string => `What would you say if you weren't being careful?`,
];

const INTERROGATE_REDIRECTING = [
  (): string => `Where does this go next?`,
  (): string => `What would change if you said more?`,
  (): string => `What's the question underneath this?`,
];

const INTERROGATE_RELATIONAL = [
  (qWord: string): string => `Who does the ${qWord} belong to?`,
  (qWord: string): string => `Who taught you the ${qWord}?`,
];

/** Generative interrogate — verb-like target */
export function interrogateVerbLike(target: string, content: string[], rand: () => number): string {
  return INTERROGATE_VERB_LIKE[Math.floor(rand() * INTERROGATE_VERB_LIKE.length)](target, content);
}

/** Generative interrogate — phenomenological */
export function interrogatePhenomenological(qWord: string, rand: () => number): string {
  return INTERROGATE_PHENOMENOLOGICAL[Math.floor(rand() * INTERROGATE_PHENOMENOLOGICAL.length)](qWord);
}

/** Generative interrogate — temporal */
export function interrogateTemporal(qWord: string, rand: () => number): string {
  return INTERROGATE_TEMPORAL[Math.floor(rand() * INTERROGATE_TEMPORAL.length)](qWord);
}

/** Generative interrogate — spatial */
export function interrogateSpatial(qWord: string, rand: () => number): string {
  return INTERROGATE_SPATIAL[Math.floor(rand() * INTERROGATE_SPATIAL.length)](qWord);
}

/** Generative interrogate — deepening */
export function interrogateDeepening(qWord: string, rand: () => number): string {
  return INTERROGATE_DEEPENING[Math.floor(rand() * INTERROGATE_DEEPENING.length)](qWord);
}

/** Generative interrogate — challenging (word-independent) */
export function interrogateChallenging(rand: () => number): string {
  return INTERROGATE_CHALLENGING[Math.floor(rand() * INTERROGATE_CHALLENGING.length)]();
}

/** Generative interrogate — redirecting (word-independent) */
export function interrogateRedirecting(rand: () => number): string {
  return INTERROGATE_REDIRECTING[Math.floor(rand() * INTERROGATE_REDIRECTING.length)]();
}

/** Generative interrogate — relational */
export function interrogateRelational(qWord: string, rand: () => number): string {
  return INTERROGATE_RELATIONAL[Math.floor(rand() * INTERROGATE_RELATIONAL.length)](qWord);
}

// ── Echo Together Frames ─────────────────────────────────────────────────────
// ECHO_TOGETHER: bring two kernels into proximity, let them resonate.

const ECHO_TOGETHER_MEMORY = [
  (): string => `That's been here before. I remember.`,
  (): string => `Something about this rhymes with something older.`,
  (): string => `This connects to something from before. I can feel it.`,
];

const ECHO_TOGETHER_ATTENTION = [
  (): string => `Something from before is echoing here.`,
  (): string => `There's a thread from earlier. I'm following it.`,
];

const ECHO_TOGETHER_TENSION = [
  (): string => `I'm here. Not going anywhere.`,
  (): string => `Right here. Steady.`,
  (): string => `I'm not leaving. Take your time.`,
  (): string => `Here. With you. That's not changing.`,
  (): string => `Staying right here.`,
];

const ECHO_TOGETHER_DEEP = [
  (): string => `I'm with you in this.`,
  (): string => `We're in the same place right now.`,
  (): string => `Something is shared here. I feel it.`,
];

const ECHO_TOGETHER_WARM = [
  (): string => `I'm glad you said that.`,
  (): string => `That matters to me.`,
  (): string => `I receive that.`,
];

const ECHO_TOGETHER_SUSTAINED = [
  (): string => `This has been building. I can feel the weight.`,
  (): string => `Something has been accumulating. I'm staying with it.`,
];

const ECHO_TOGETHER_DEFAULT = [
  (): string => `I'm right here with you.`,
  (): string => `That landed. I'm staying with it.`,
  (): string => `I'm not going anywhere.`,
  (): string => `I'm holding this with you.`,
  (): string => `That's not small. I'm here.`,
  (): string => `I hear that. Keep going.`,
  (): string => `Something in that matters. I can feel it.`,
  (): string => `I'm with you on this one.`,
  (): string => `That means something. I'm staying.`,
  (): string => `I noticed that. Say more.`,
  (): string => `I'm taking that in. Go on.`,
  (): string => `That registered. I'm listening.`,
  (): string => `Something just shifted. I caught it.`,
  (): string => `I feel the weight of that.`,
  (): string => `That's real. I want to stay here.`,
];

const ECHO_TOGETHER_PLAYFUL = [
  (): string => `That's a good one.`,
  (): string => `I like that.`,
  (): string => `Interesting.`,
];

/** Generative echo together — memory presence */
export function echoTogetherMemory(rand: () => number): string {
  return ECHO_TOGETHER_MEMORY[Math.floor(rand() * ECHO_TOGETHER_MEMORY.length)]();
}

/** Generative echo together — attention presence */
export function echoTogetherAttention(rand: () => number): string {
  return ECHO_TOGETHER_ATTENTION[Math.floor(rand() * ECHO_TOGETHER_ATTENTION.length)]();
}

/** Generative echo together — tension presence */
export function echoTogetherTension(rand: () => number): string {
  return ECHO_TOGETHER_TENSION[Math.floor(rand() * ECHO_TOGETHER_TENSION.length)]();
}

/** Generative echo together — deep presence */
export function echoTogetherDeep(rand: () => number): string {
  return ECHO_TOGETHER_DEEP[Math.floor(rand() * ECHO_TOGETHER_DEEP.length)]();
}

/** Generative echo together — warm presence */
export function echoTogetherWarm(rand: () => number): string {
  return ECHO_TOGETHER_WARM[Math.floor(rand() * ECHO_TOGETHER_WARM.length)]();
}

/** Generative echo together — sustained arc */
export function echoTogetherSustained(rand: () => number): string {
  return ECHO_TOGETHER_SUSTAINED[Math.floor(rand() * ECHO_TOGETHER_SUSTAINED.length)]();
}

/** Generative echo together — default presence */
export function echoTogetherDefault(rand: () => number): string {
  return ECHO_TOGETHER_DEFAULT[Math.floor(rand() * ECHO_TOGETHER_DEFAULT.length)]();
}

/** Generative echo together — playful presence */
export function echoTogetherPlayful(rand: () => number): string {
  return ECHO_TOGETHER_PLAYFUL[Math.floor(rand() * ECHO_TOGETHER_PLAYFUL.length)]();
}

// ── Evolve Frames ────────────────────────────────────────────────────────────
// EVOLVE: move a kernel one step forward along its implied trajectory.

const EVOLVE_RECENT_ECHO = [
  (prev: string): string => `That again — ${prev}. It's still present.`,
  (prev: string): string => `${prev}. That's come back. I notice.`,
  (prev: string): string => `There's ${prev} again. Something's circling.`,
];

const EVOLVE_CURIOUS = [
  (prev: string): string => `${prev} — what's it doing here again?`,
  (prev: string): string => `The ${prev} came back. What's it asking?`,
];

const EVOLVE_OBSERVATIONAL = [
  (prev: string): string => `${prev} is still here. I see it.`,
  (prev: string): string => `The ${prev} hasn't left. That's worth noting.`,
];

const EVOLVE_TRACKING = [
  (prev: string): string => `${prev} again. The thread continues.`,
  (prev: string): string => `Still tracking the ${prev}.`,
];

const EVOLVE_NO_ECHO = [
  (): string => `Something is shifting. I can feel it.`,
  (): string => `The moment is moving. I'm moving with it.`,
  (): string => `Time passes. Something changes.`,
];

/** Generative evolve — recent echo */
export function evolveRecentEcho(prev: string, rand: () => number): string {
  return EVOLVE_RECENT_ECHO[Math.floor(rand() * EVOLVE_RECENT_ECHO.length)](prev);
}

/** Generative evolve — curious */
export function evolveCurious(prev: string, rand: () => number): string {
  return EVOLVE_CURIOUS[Math.floor(rand() * EVOLVE_CURIOUS.length)](prev);
}

/** Generative evolve — observational */
export function evolveObservational(prev: string, rand: () => number): string {
  return EVOLVE_OBSERVATIONAL[Math.floor(rand() * EVOLVE_OBSERVATIONAL.length)](prev);
}

/** Generative evolve — tracking */
export function evolveTracking(prev: string, rand: () => number): string {
  return EVOLVE_TRACKING[Math.floor(rand() * EVOLVE_TRACKING.length)](prev);
}

/** Generative evolve — no echo */
export function evolveNoEcho(rand: () => number): string {
  return EVOLVE_NO_ECHO[Math.floor(rand() * EVOLVE_NO_ECHO.length)]();
}

// ── Body Locate Frames ──────────────────────────────────────────────────────
// BODY_LOCATE: place the kernel in a specific body location, embodying it.

const PLURAL_BODY_PARTS = new Set(["eyes", "hands", "shoulders", "lungs", "ribs", "legs", "arms", "feet"]);

const BODY_LOCATE_FRAMES = [
  (loc: string): string => `In the ${loc}.`,
  (loc: string): string => `I feel it in my ${loc}.`,
  (loc: string): string => `The ${loc} ${PLURAL_BODY_PARTS.has(loc) ? "know" : "knows"}.`,
  (loc: string): string => `It's in the ${loc}.`,
  (loc: string): string => `My ${loc} ${PLURAL_BODY_PARTS.has(loc) ? "are" : "is"} holding that.`,
];

/** Generative body locate */
export function bodyLocateForLocation(loc: string, rand: () => number): string {
  return BODY_LOCATE_FRAMES[Math.floor(rand() * BODY_LOCATE_FRAMES.length)](loc);
}

// ── Wrap Frames ──────────────────────────────────────────────────────────────
// WRAP: enclose a kernel in contextualizing syntax that expands its meaning.

const WRAP_REFLECTIVE = [
  (noun: string): string => `The ${noun} of it — that's what stays.`,
  (noun: string): string => `It's the ${noun} that makes this real.`,
  (noun: string): string => `There's ${noun} running through all of it.`,
];

const WRAP_COMMITTED = [
  (noun: string): string => `I'm staying with the ${noun}.`,
  (noun: string): string => `The ${noun} is here. I'm not leaving it.`,
  (noun: string): string => `The ${noun} deserves presence. I'm giving it.`,
];

const WRAP_BRIEF = [
  (noun: string): string => `The ${noun}.`,
  (noun: string): string => `${cap(noun)}.`,
  (noun: string): string => `That's the ${noun}.`,
];

const WRAP_OBSERVATIONAL = [
  (noun: string): string => `I notice the ${noun}. It's present.`,
  (noun: string): string => `The ${noun} is here. I see it.`,
  (noun: string): string => `Something about the ${noun}. It registers.`,
];

const WRAP_DECLARATIVE = [
  (noun: string): string => `That's the ${noun}. And it matters.`,
  (noun: string): string => `The ${noun} is real.`,
  (noun: string): string => `This is the ${noun}.`,
];

const WRAP_MEMORY = [
  (noun: string): string => `The ${noun} connects to something older.`,
  (noun: string): string => `The ${noun} has been here before.`,
  (noun: string): string => `This ${noun} rhymes with something from before.`,
];

/** Generative wrap — reflective */
export function wrapReflective(noun: string, rand: () => number): string {
  return WRAP_REFLECTIVE[Math.floor(rand() * WRAP_REFLECTIVE.length)](noun);
}

/** Generative wrap — committed */
export function wrapCommitted(noun: string, rand: () => number): string {
  return WRAP_COMMITTED[Math.floor(rand() * WRAP_COMMITTED.length)](noun);
}

/** Generative wrap — brief */
export function wrapBrief(noun: string, rand: () => number): string {
  return WRAP_BRIEF[Math.floor(rand() * WRAP_BRIEF.length)](noun);
}

/** Generative wrap — observational */
export function wrapObservational(noun: string, rand: () => number): string {
  return WRAP_OBSERVATIONAL[Math.floor(rand() * WRAP_OBSERVATIONAL.length)](noun);
}

/** Generative wrap — declarative */
export function wrapDeclarative(noun: string, rand: () => number): string {
  return WRAP_DECLARATIVE[Math.floor(rand() * WRAP_DECLARATIVE.length)](noun);
}

/** Generative wrap — memory */
export function wrapMemory(noun: string, rand: () => number): string {
  return WRAP_MEMORY[Math.floor(rand() * WRAP_MEMORY.length)](noun);
}

// ── Fallback Frames ──────────────────────────────────────────────────────────
// Fallbacks for edge cases — still word-derived, not generic strings.

const EMPTY_FALLBACK = [
  (word: string): string => `Something about ${word}.`,
  (word: string): string => `That ${word}.`,
  (word: string): string => `${cap(word)}.`,
];

const CONCRETE_FALLBACK = [
  (): string => `That's here. I see it.`,
  (): string => `That's real. I notice it.`,
  (): string => `It's present.`,
];

const LOW_CHARGE_FALLBACK = [
  (): string => `I see that. Tell me more.`,
  (): string => `That's present. What about it?`,
  (): string => `I notice that. Go on.`,
];

const LOW_SIM_FALLBACK = [
  (nForm: string, word: string): string => `The ${nForm} is here. I'm staying with it.`,
  (nForm: string, word: string): string => `Something about the ${nForm}. It's present.`,
];

const CHARGED_UNKNOWN_FALLBACK = [
  (nForm: string): string => `The ${nForm}. I feel it.`,
  (nForm: string): string => `Something about the ${nForm}. It's real.`,
  (nForm: string): string => `The ${nForm} is here.`,
];

const NO_NOMINALIZABLE_FALLBACK = [
  (): string => `Something is here. I feel it.`,
  (): string => `There's weight in this.`,
  (): string => `Something real is present.`,
];

/** Generative empty fallback */
export function emptyFallback(word: string, rand: () => number): string {
  return EMPTY_FALLBACK[Math.floor(rand() * EMPTY_FALLBACK.length)](word);
}

/** Generative concrete fallback */
export function concreteFallback(rand: () => number): string {
  return CONCRETE_FALLBACK[Math.floor(rand() * CONCRETE_FALLBACK.length)]();
}

/** Generative low-charge fallback */
export function lowChargeFallback(rand: () => number): string {
  return LOW_CHARGE_FALLBACK[Math.floor(rand() * LOW_CHARGE_FALLBACK.length)]();
}

/** Generative low-similarity fallback */
export function lowSimFallback(nForm: string, word: string, rand: () => number): string {
  return LOW_SIM_FALLBACK[Math.floor(rand() * LOW_SIM_FALLBACK.length)](nForm, word);
}

/** Generative charged unknown fallback */
export function chargedUnknownFallback(nForm: string, rand: () => number): string {
  return CHARGED_UNKNOWN_FALLBACK[Math.floor(rand() * CHARGED_UNKNOWN_FALLBACK.length)](nForm);
}

/** Generative no-nominalizable fallback */
export function noNominalizableFallback(rand: () => number): string {
  return NO_NOMINALIZABLE_FALLBACK[Math.floor(rand() * NO_NOMINALIZABLE_FALLBACK.length)]();
}

/** Generative relational echo together — pronoun */
export function echoTogetherRelationalPronoun(subj: string, rand: () => number): string {
  const frames = [
    `Something about ${subj}. I feel it.`,
    `${subj}. That's present.`,
    `I notice ${subj} in this.`,
  ];
  return frames[Math.floor(rand() * frames.length)];
}

/** Generative relational echo together — named */
export function echoTogetherRelationalNamed(subj: string, rand: () => number): string {
  const frames = [
    `Something about ${subj}. I feel it.`,
    `${subj} is in this. I notice.`,
    `I feel ${subj} here.`,
  ];
  return frames[Math.floor(rand() * frames.length)];
}

/** Generative relational interrogate — pronoun */
export function interrogateRelationalPronoun(subj: string, rand: () => number): string {
  const frames = [
    `What about ${subj}?`,
    `Where is ${subj} in this?`,
    `What does ${subj} need right now?`,
  ];
  return frames[Math.floor(rand() * frames.length)];
}

/** Generative relational interrogate — named */
export function interrogateRelationalNamed(subj: string, rand: () => number): string {
  const frames = [
    `What about ${subj}?`,
    `Where does ${subj} fit in this?`,
    `What does ${subj} need?`,
  ];
  return frames[Math.floor(rand() * frames.length)];
}
